import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { garmentColors } from "@/components/shirts/ApparelCanvas";
import { uploadArtwork } from "@/components/signs/useSignConfig";

const ALL_VIEWS = ["front", "back", "left_sleeve", "right_sleeve"];

export function useShirtConfig(product, { store = null } = {}) {
    const [sizes, setSizes] = useState({});
    const [arts, setArts] = useState({});
    const [busy, setBusy] = useState({});
    const [texts, setTexts] = useState({});
    const [view, setView] = useState("front");
    const [color, setColor] = useState(null);
    const [deco, setDeco] = useState({});
    const [quote, setQuote] = useState(null);
    const [pricing, setPricing] = useState(false);
    const slug = product?.slug;
    const timer = useRef(null);

    const views = useMemo(() => ALL_VIEWS.filter((v) => (product?.mockups || {})[v]), [product]);

    useEffect(() => {
        setSizes({});
        setArts({});
        setTexts({});
        setView("front");
        setColor(product ? garmentColors(product)[0] : null);
        setDeco(product?.decoration === "embroidery"
            ? { stitch_tier: (product.pricing?.stitch_tiers || [])[0]?.id, digitizing: true }
            : product?.decoration === "screen_print" ? { colors: {} } : {});
    }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

    const locations = useMemo(
        () => views.filter((v) => arts[v]?.file || (texts[v] || []).some((t) => t.text?.trim())),
        [views, arts, texts]
    );

    useEffect(() => {
        if (!slug) return;
        clearTimeout(timer.current);
        setPricing(true);
        timer.current = setTimeout(() => {
            api.post("/shirts/pricing", { slug, sizes, locations, decoration: deco, store: store || undefined })
                .then(({ data }) => setQuote(data)).catch(() => { }).finally(() => setPricing(false));
        }, 260);
        return () => clearTimeout(timer.current);
    }, [slug, JSON.stringify(sizes), JSON.stringify(locations), JSON.stringify(deco), store]); // eslint-disable-line react-hooks/exhaustive-deps

    const setSize = (key, value) => setSizes((p) => ({ ...p, [key]: value }));
    const setArt = (v, next) => setArts((p) => ({ ...p, [v]: next }));
    const viewTexts = texts[view] || [];

    /** Upload one image for a view and size it to fill the print area. Throws so the caller can toast. */
    const pickArt = async (v, file) => {
        if (!file) return null;
        const area = (product?.print_areas || {})[v] || { max_width_in: 15.5, max_height_in: 20 };
        const url = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
        setBusy((p) => ({ ...p, [v]: true }));
        try {
            const ratio = url ? await new Promise((res) => {
                const img = new Image();
                img.onload = () => res((img.naturalWidth || 1) / (img.naturalHeight || 1));
                img.onerror = () => res(1);
                img.src = url;
            }) : 1;
            const rec = await uploadArtwork(file);
            const fitW = Math.min(area.max_width_in, area.max_height_in * ratio);
            setArt(v, { file: rec, url, ratio, widthIn: Math.round(fitW * 10) / 10, x: 0, y: 0 });
            return rec;
        } finally {
            setBusy((p) => ({ ...p, [v]: false }));
        }
    };

    const addText = () => {
        setTexts((p) => ({
            ...p,
            [view]: [...(p[view] || []), { text: "YOUR TEXT", font: "Anton", color: "#ffffff", heightIn: 2.5, x: 0, y: 0, tracking: 0, outline: null }],
        }));
        return viewTexts.length;
    };
    const setText = (idx, next) => setTexts((p) => {
        const list = [...(p[view] || [])];
        list[idx] = next;
        return { ...p, [view]: list };
    });
    const removeText = (idx) => setTexts((p) => ({ ...p, [view]: (p[view] || []).filter((_, i) => i !== idx) }));

    return {
        sizes, setSize, arts, setArt, pickArt, busy, view, setView, views, locations, quote, pricing,
        color, setColor, texts, viewTexts, addText, setText, removeText, deco, setDeco,
    };
}

export { ALL_VIEWS as VIEWS };
